import { MdClose, MdDelete, MdOutlineFingerprint } from "react-icons/md";
import useAuth from "../../hooks/useAuth";
import useCurrentUser from "../../hooks/useCurrentUser";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import Modal from "../Modal/Modal";
import ModalFooter from "../Modal/ModalFooter";
import { useMemo, useState } from "react";
import InfoBlock from "../InfoBlock/InfoBlock";
import useMobile from "src/hooks/useMobile";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import { ICON_SIZES } from "src/constants/icons";
import { EButtonKind } from "../Button/Button.types";

interface IRegisterPassKeyModal {
  onClose: () => void;
}

const RegisterPassKeyModal: React.FC<IRegisterPassKeyModal> = ({ onClose }) => {
  const { registerPassKey } = useAuth();
  const { userInfo } = useCurrentUser();
  const { t } = useTranslation(["common"]);
  const { isMobile } = useMobile();
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);

  const hasPasskey = useMemo(() => {
    return !!userInfo?.credential?.id;
  }, [userInfo]);

  return (
    <>
      <Modal
        onEscape={() => onClose()}
        className="md:!max-w-[500px] md:!w-full"
      >
        <Modal.Header
          title={t("modals:passwordless_access.title")}
          onClose={() => onClose()}
        />
        <Modal.Body>
          <div className={clsx(["flex", "flex-col", "gap-4"])}>
            <div className={clsx(["flex", "w-full", "text-sm", "md:text-sm"])}>
              {t("modals:passwordless_access.description")}
            </div>
            {hasPasskey && (
              <InfoBlock
                displayMobileIcon
                message={t("modals:passwordless_access.registered_message")}
                title={t("modals:passwordless_access.registered_title")}
                buttons={
                  isMobile
                    ? []
                    : [
                        {
                          text: "",
                          tooltip: t("common:tooltips.delete"),
                          onClick: () => setShowDeleteDialog(true),
                          icon: <MdDelete size={ICON_SIZES.SM} />,
                          kind: EButtonKind.DANGER,
                        },
                      ]
                }
              />
            )}
          </div>
        </Modal.Body>
        <ModalFooter
          leftContent={
            isMobile ? (
              <ButtonGroup
                buttons={[
                  {
                    text: "",
                    tooltip: t("common:tooltips.delete"),
                    onClick: () => setShowDeleteDialog(true),
                    icon: <MdDelete size={ICON_SIZES.SM} />,
                    kind: EButtonKind.DANGER,
                  },
                ]}
              />
            ) : null
          }
          actions={[
            {
              onClick: async () => {
                await registerPassKey({ email: userInfo.email });
              },
              text: t("common:buttons.register"),
              icon: <MdOutlineFingerprint size={ICON_SIZES.SM} />,
              kind: EButtonKind.SAVE,
              hide: hasPasskey,
            },
            {
              text: t("common:buttons.close"),
              icon: <MdClose size={ICON_SIZES.SM} />,
              onClick: () => onClose(),
              kind: EButtonKind.NORMAL,
            },
          ]}
        />
      </Modal>
      {showDeleteDialog && (
        <ConfirmDialog
          description={t("modals:passwordless_access.questions.delete")}
          onClose={() => setShowDeleteDialog(false)}
          onConfirm={() => {}}
          confirmKind={EButtonKind.DANGER}
          title={t("dialogs:titles.delete")}
        />
      )}
    </>
  );
};

export default RegisterPassKeyModal;
